import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { forkJoin, Observable, Subject } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { SignalrNotification } from '../../../shared/entities/SignalrNotification.class';
import { GridSearchItem } from '../entities/grid-search-item.class';
import { ProductsResponse } from '../entities/products-response.class';
import { Product } from '../entities/products.class';
import { SortColumn, SortDirection } from '../entities/sort.enum';
import { ProductNotificationEvent } from '../entities/product-notification-event';
import { PaymentDetailsService } from './payment-details.service';
import { QuoteDetailsService } from './quote-details.service';

@Injectable({
  providedIn: 'root'
})
export class QuoteToolsService {
  private productData: ProductsResponse = new ProductsResponse();
  private errorIconArray = ['error', 'warning', 'info'];
  quoteId = '';
  private accountManager;
  pageSize = 50;
  productList$ = new Subject<Product[]>();
  productCount$ = new Subject<number>();
  errorCount$ = new Subject<number>();
  pageIndex$ = new Subject<number>();
  showOemSku$ = new Subject<boolean>();
  showCustomTool$ = new Subject<boolean>();
  atpChecked$ = new Subject<boolean>();
  refreshToolsList$ = new Subject<any>();
  itemizeInProgress$ = new Subject<any>();
  toolEditInProgress$ = new Subject<boolean>();
  productListHasChanges$ = new Subject();
  hasProductsFromConsigned$ = new Subject();
  updateCWInProgress$ = new Subject<boolean>();
  updateItemInProgress$ = new Subject<boolean>();
  checkForResetQuoteRestored$ = new Subject<boolean>();
  focusPartNumberInput$ = new Subject<boolean>();
  paymentSummaryUpdated$ = new Subject<boolean>();
  priceMatrixListRefresh$ = new Subject<boolean>();
  atpRefresh$ = new Subject();
  atpIsRefreshing$ = new Subject<boolean>();
  addPriceAsTool$ = new Subject<{ productNumber: string, quantity: number }>();
  exportLoading$ = new Subject<boolean>();

  private urls = {
    productTemplateValues: '/api/product/templatevalues',
    productNetPriceChanges: '/api/product/unitNetPriceChange',
    ignoreNotification: '/api/notification/ignore',
    supersedeItem: '/api/notification/supersede',
    updateOemSkuFlag: '/api/accountmanager/oemsku',
    updateOemSkuProduct: '/api/notification/oemSku',
    updateProductQuantity: '/api/notification/updateproductquantity',
    deleteConsignedProductsFromQuote: '/api/consigned/deleteQuoteProducts',
    atpNotification: '/api/notification/atp',
    updateCustomTool: '/api/accountmanager/customTool',
    saveCustomTool: '/api/product/customTool/edit',
    saveATP: '/api/quote/atp/edit',
    deleteToolUrl: '/api/product/delete',
    createEditToolUrl: '/api/product/create/edit',
    printQuoteUrl: '/api/quote/printQuote',
    exportQuoteUrl: '/api/quote/excelExport',
    emailQuoteUrl: '/api/quote/emailQuote',
    uploadTemplateUrl: '/api/upload/template',
    uploadExcelUrl: '/api/upload/products',
    productListUrl: '/api/product/list',
    reorderProductsUrl: '/api/product/reorder',
    importQuotesOrOrders: '/api/quote/clone',
    refreshAtpUrl: '/api/product/refreshAtp',
    getProductsUrl: '/api/product/search',
    productShipDate: '/api/product/estimatedShipDate'
  };
  private errorCount = 0;
  private exportInProgress = false;

  constructor(
    private apiService: ApiService,
    private exportUtilsService: ExportUtilsService,
    private quoteDetailsService: QuoteDetailsService,
    private paymentDetailsService: PaymentDetailsService,
    private notificationService: NotificationService
  ) { }

  /**
   * Makes the api call to update the flag value. If successfull, triggers a column redraw.
   * @param value boolean The new value for the checkbox
   */
  updateOemSkuFlag(value) {
    const payload = {
      id: this.accountManager.id,
      showOemSku: value
    };
    this.apiService.post(this.urls.updateOemSkuFlag, payload)
      .subscribe((result: AccountManager) => this.showOemSku$.next(result.showOemSku));
  }

  updateCustomToolFlag(value) {
    const payload = {
      id: this.accountManager.id,
      showCustomTool: value
    };
    this.apiService.post(this.urls.updateCustomTool, payload)
      .subscribe((result: AccountManager) => this.showCustomTool$.next(result.showCustomTool));
  }

  updateATPFlag(customerId: string, leadId: string, atp: boolean) {
    this.apiService.post(this.urls.saveATP, { quoteId: this.quoteId, customerId, leadId, atp }).subscribe();
  }

  saveCustomToolInfo(customToolInfo, element?: Product) {
    this.apiService.post(this.urls.saveCustomTool, customToolInfo).subscribe((customToolResponse: Product) => {
      const oldToolHasError = element.hasError;
      this.mapTool(element, customToolResponse);
      element.updateCustomToolInProgress = false;
      this.setNotificationIcon(element);
      this.adjustErrorCount(element.hasError, oldToolHasError);
      this.productListHasChanges$.next(true);
      this.focusPartNumberInput$.next(true);
    });
  }

  /**
   * Sets the OEM SKU value
   * @param accountManager the new account manager
   */
  setAccountManagerData(accountManager: AccountManager) {
    // only update when needed
    this.showOemSku$.next(accountManager.showOemSku);
    this.showCustomTool$.next(accountManager.showCustomTool);
    // make as observable, trigger in quoteDetailsComponent. Subscribe in product.grid.component onInit
    this.accountManager = accountManager;
  }

  /**
   * Updates the product data.
   * @param eventDetails the object details
   */
  updateOemSkuProduct(eventDetails) {
    eventDetails.tool.isLoading = true;
    const payload = {
      id: eventDetails.notification.id,
      oemSkuProductNumber: eventDetails.extraParams.oemSkuValue
    };
    this.apiService.post(this.urls.updateOemSkuProduct, payload).subscribe({
      next: (result: Product) => {
        const newTool: Product = eventDetails.tool;
        const oldItemHasError = this.itemHasError(newTool);
        this.mapTool(newTool, result);
        this.setNotificationIcon(newTool);
        this.adjustErrorCount(newTool.hasError, oldItemHasError);
      },
      error: null,
      complete: () => this.productListHasChanges$.next(true)
    });
  }

  /**
   * Sets the quote id in the service. Will be used for most of the api calls
   * @param quoteId string the quote id
   */
  setQuoteId(quoteId) {
    this.quoteId = quoteId;
  }

  /**
   * Gets static template values
   * @param callback - The callback
   */
  getStaticValues(callback: (...args: any[]) => any) {
    forkJoin([
      this.apiService.get(this.urls.productTemplateValues),
    ]).subscribe(result => callback(result));
  }

  /**
   * Downloads the Excel Template
   */
  downloadExcelTemplate() {
    this.apiService.getBlob(this.urls.uploadTemplateUrl)
      .subscribe((data: ArrayBuffer) => this.exportUtilsService.downloadFile(data, 'IMP_ProductUpload.xlsx'));
  }

  /**
   * Uploads the Excel file
   * @param fileList - The list of files
   * @param notification - SignalR notification object
   * @param callback - A callback to be triggered when upload is done
   */
  uploadExcel(fileList: FileList, notification: SignalrNotification, callback: (...args: any[]) => any) {
    const file: File = fileList[0];
    const formData: FormData = new FormData();
    if (this.quoteId && this.quoteId.length > 0) {
      formData.append('quoteId', this.quoteId);
    }
    formData.append('ToolUploadTemplate', file, file.name);
    formData.append('connectionId', notification.connectionId);
    formData.append('callbackMethodName', notification.callbackMethodName);

    return this.apiService.post(this.urls.uploadExcelUrl, formData)
      .subscribe(() => callback(), error => callback(error));
  }

  /**
   * Handles the productList value update
   * @param pageToDisplay - The page to display
   * @param sortBy - Sort by column
   * @param sortDirection - Sort by column
   * @param callback - Callback to be called when done
   * @param errorHandler - Callback to be called if error
   */
  getProductList(
    pageToDisplay: number,
    sortBy?: number,
    sortDirection?: number,
    callback?: (...args: any[]) => any,
    errorHandler?: (...args: any[]) => any) {

    const payload: any = { page: pageToDisplay };
    if (!this.quoteDetailsService.isExistingQuote(this.quoteId)) {
      setTimeout(() => this.handleProductsResponse({ count: 0, errors: 0, products: [] }));
      return;
    }
    payload.quoteId = this.quoteId;
    if (sortBy && sortDirection) {
      payload.Sort = {
        SortBy: sortBy,
        Direction: sortDirection
      };
    }

    this.apiService.post<ProductsResponse>(this.urls.productListUrl, payload)
      .subscribe(serverData => {
        this.handleProductsResponse(serverData);
        this.setNotificationIconAll(this.productData.products);
        if (callback) {
          callback();
        }
      }, error => {
        if (errorHandler) {
          errorHandler(error);
        }
      });
  }

  getNotificationIconForProduct(product: Product) {
    return this.errorIconArray[this.getNotificationType(product) - 1];
  }

  private getNotificationType(product: Product) {
    let errorCode = 100;
    if (product.notifications) {
      product.notifications.forEach(notification => {
        errorCode = errorCode > notification.messageTypeId ?
          notification.messageTypeId : errorCode;
      });
    }
    return errorCode;
  }

  /**
   * Sets the notification icon (if any) based on the tool's data
   * @param productList the product list
   */
  private setNotificationIconAll(productList: Product[]) {
    productList.forEach(product => this.setNotificationIcon(product));
  }

  /**
   * Sets the notification icon (if any) based on the tool's data
   * @param productList the product list
   */
  setNotificationIcon(product: Product) {
    product.notificationIcon = this.getNotificationIconForProduct(product);
    product.hasError = this.itemHasError(product);
    product.hasExpiredPricing = this.itemHasExpiredPricing(product);
  }

  removeNotificationFromProduct(tool, notificationId) {
    return tool.notifications.filter(notification => notification.id !== notificationId);
  }

  ignoreNotification(event: ProductNotificationEvent, callback?: (...args: any[]) => any) {
    event.tool.isLoading = true;
    const notificationId = event.notification.id;
    this.apiService.post(this.urls.ignoreNotification, { id: notificationId }).subscribe(() => {
      event.tool.notifications = this.removeNotificationFromProduct(event.tool, notificationId);
      this.setNotificationIcon(event.tool);
      event.tool.isLoading = false;
      if (callback) {
        callback();
      }
    });
  }

  supersedeItem(event: ProductNotificationEvent) {
    event.tool.isLoading = true;
    const notificationId = event.notification.id;
    this.apiService.post(this.urls.supersedeItem, { id: notificationId }).subscribe({
      next: (result: Product) => {
        // replace the item with the one in "result"
        // similar to replacing the product after adding / editing.
        const oldHasError = event.tool.hasError;
        this.mapTool(event.tool, result);
        this.setNotificationIcon(event.tool);
        this.adjustErrorCount(event.tool.hasError, oldHasError);
      },
      error: null,
      complete: () => this.productListHasChanges$.next(true)
    }
    );
  }

  // Update product quantity
  updateQuantity(event: ProductNotificationEvent) {
    event.tool.isLoading = true;
    const notificationId = event.notification.id;
    this.apiService.post(this.urls.updateProductQuantity, { id: notificationId }).subscribe({
      next: (result: Product) => {
        const oldHasError = event.tool.hasError;
        this.mapTool(event.tool, result);
        this.setNotificationIcon(event.tool);
        this.adjustErrorCount(event.tool.hasError, oldHasError);
      },
      error: null,
      complete: () => this.productListHasChanges$.next(true)
    }
    );
  }

  /**
   * Handles the products reorder update
   * @param pageToDisplay - The page to display
   * @param sortBy - Sort by column
   * @param sortDirection - Sort direction, ascending or descending
   */
  reorderProducts(pageToDisplay: number, sortBy: number, sortDirection: number) {
    const payload: any = { page: pageToDisplay };
    payload.quoteId = this.quoteId;
    payload.Sort = {
      SortBy: sortBy,
      Direction: sortDirection
    };

    this.apiService.post<ProductsResponse>(this.urls.reorderProductsUrl, payload)
      .subscribe(serverData => {
        this.handleProductsResponse(serverData);
        this.setNotificationIconAll(this.productData.products);
      });
  }

  /**
   * Handles the products received from response
   * @param serverData - The response from server
   */
  private handleProductsResponse(serverData) {
    this.productData = serverData;
    if (serverData.count > 0) {
      this.checkIfItemsFromCW(serverData);
    } else {
      this.hasProductsFromConsigned$.next(false);
    }
    this.productData.products.unshift(new Product());
    this.errorCount = this.productData.errors;
    this.errorCount$.next(this.errorCount);
    this.emitProductChanges();
  }

  /**
   * Checks if there are products from consigned warehouse
   * @param serverData - the response from server with products and count
   */
  private checkIfItemsFromCW(serverData) {
    if (serverData.products[0].consignedWarehouse) {
      this.hasProductsFromConsigned$.next(true);
    } else {
      this.hasProductsFromConsigned$.next(false);
    }
  }

  /**
   * Adds a new tool
   * @param tool - The tool to be added
   * @returns an {Observable<any>}
   */
  addTool(tool: Product) {
    tool.quoteId = this.quoteId;
    tool.quantity = Number(tool.quantity);
    this.toolEditInProgress$.next(true);

    return this.apiService.post<Product>(this.urls.createEditToolUrl, tool);
  }

  /**
   * Deletes a tool
   * @param tool - The tool to be added
   * @param currentPage - The current page
   */
  deleteTool(tool: Product, currentPage: number) {
    this.apiService.post<Product>(this.urls.deleteToolUrl, {
      id: tool.id,
      calculateTotals: tool.calculateTotals
    }).subscribe(() => {
      // check if the item had errors, if so, decrement the error count
      if (this.itemHasError(tool)) {
        this.errorCount = this.errorCount - 1;
        this.errorCount$.next(this.errorCount);
      }
      this.processDelete(currentPage);
    }, () => {
      tool.isLoading = false;
      tool.deletionInProgress = false;
    }, () => this.productListHasChanges$.next(true));
  }

  deleteToolWithQueueRequest(tool: Product, currentPage: number, sort?: Sort) {
    const payload = {
      id: tool.id,
      calculateTotals: tool.calculateTotals
    };
    this.toolEditInProgress$.next(true);
    this.apiService
      .queueRequest(this.urls.deleteToolUrl, payload,
        () => this.deleteToolSuccessCallback(tool),
        'post',
        () => {
          this.toolEditInProgress$.next(false);
          this.checkForResetQuoteRestored$.next(true);
          this.processDelete(currentPage, sort);
          this.paymentDetailsService.calculatePaymentSummary(this.quoteId);
        },
        () => this.deleteToolErrorCallback(tool));
  }

  private deleteToolSuccessCallback(tool: Product) {
    if (this.itemHasError(tool)) {
      this.errorCount = this.errorCount - 1;
      this.errorCount$.next(this.errorCount);
    }
  }

  private deleteToolErrorCallback(tool) {
    this.toolEditInProgress$.next(false);
    tool.isLoading = false;
    tool.deletionInProgress = false;
  }

  /**
   * Edits a tool
   * @param tool - The tool to be edited
   * @param currentPage - The current page by which to get the products if reorder by line number
   * @param errorCallback - the callback if API call fails
   */
  editTool(tool: Product, currentPage?: number, errorCallback?: (...args: any[]) => any, callback?: (...args: any[]) => any) {
    tool.quoteId = this.quoteId;
    tool.quantity = Number(tool.quantity);
    if (Math.floor(tool?.netPrice).toString().length > 8) {
      tool.netPrice = Math.floor(tool.netPrice / 10) + Number(tool?.netPrice?.toString().split('.')[1]) / 100;
    }
    tool.netPrice = Number(tool.netPrice);
    this.toolEditInProgress$.next(true);
    this.apiService.post<Product>(this.urls.createEditToolUrl, tool)
      .subscribe((newToolData: Product) => {
        this.toolEditInProgress$.next(false);
        const oldHasError = tool.hasError;
        this.mapTool(tool, newToolData);
        if (newToolData.quantityMessage) {
          this.notificationService.showNotification(newToolData.quantityMessage, NotificationType.Warning, true);
        }
        this.setNotificationIcon(tool);
        this.adjustErrorCount(tool.hasError, oldHasError);

        if (callback) {
          callback(newToolData);
        }

        if (currentPage || currentPage >= 0) {
          this.getProductList(currentPage);
        }
        this.productListHasChanges$.next(true);
      }, () => {
        this.toolEditInProgress$.next(false);
        tool.isLoading = false;
        if (errorCallback) {
          errorCallback();
        }
      });
  }

  private itemHasError(item: Product) {
    return (this.getNotificationType(item) === 1);
  }

  private itemHasExpiredPricing(item: Product) {
    if (item.notifications) {
      return item.notifications.some(notification => notification.messageCodeId === 15);
    }
    return false;
  }

  toolUpdateCallback() {
    this.emitProductChanges();
    this.paymentSummaryUpdated$.next(false);
    this.paymentDetailsService.calculatePaymentSummary(this.quoteId, () => this.paymentSummaryUpdated$.next(true));
  }

  /**
   * Maps data to the existing tool
   * @param existingTool - Existing tool
   * @param newTool - New tool data from backend
   */
  private mapTool(existingTool: Product, newTool: Product) {
    // By adding the custom tool, there'll be products
    // without an id, products that are not found but are ok to be added
    if (existingTool) {
      existingTool.id = newTool.id;
    }
    existingTool.productNumber = newTool.productNumber;
    existingTool.countryOfOrigin = newTool.countryOfOrigin;
    existingTool.description = newTool.description;
    existingTool.quantity = newTool.quantity;
    existingTool.dbNetPrice = newTool.dbNetPrice;
    existingTool.listPrice = typeof newTool.listPrice !== 'undefined' ? Number(newTool.listPrice) : undefined;
    existingTool.netPrice = typeof newTool.netPrice !== 'undefined' ? Number(newTool.netPrice) : undefined;
    existingTool.total = newTool.total;
    existingTool.availableQuantity = newTool.availableQuantity;
    existingTool.lineNotes = newTool.lineNotes;
    existingTool.lineWeight = newTool.lineWeight;
    existingTool.notes = newTool.notes;
    existingTool.notifications = newTool.notifications;
    existingTool.carrierMessage = newTool.carrierMessage;
    existingTool.oemSku = newTool.oemSku;
    existingTool.weight = newTool.weight;
    existingTool.customTool = newTool.customTool;
    existingTool.estimatedShipDate = newTool.estimatedShipDate;
    existingTool.isLoading = false;
  }

  /**
   * Updates subjects with product list and count changes
   */
  private emitProductChanges() {
    this.productList$.next(this.productData.products);
    this.productCount$.next(this.productData.count);
    this.errorCount$.next(this.errorCount);
  }

  /**
   * Checks if the page needs to be changed and sends products update
   * @param tool - The tool requested to be added
   * @param newToolData - The new tool data from backend
   * @param currentPage - The current page
   * @param callback - The callback to be called when done
   */
  processAdd(tool: Product, newToolData: Product, currentPage: number, callback: (...args: any[]) => any) {
    this.toolEditInProgress$.next(false);
    this.mapTool(tool, new Product()); // reset new tool fields
    this.setNotificationIcon(newToolData);

    // update products list and count
    this.productData.products.push(newToolData);
    this.productData.count++;

    if (newToolData.hasError) {
      this.errorCount = this.errorCount + 1;
      this.errorCount$.next(this.errorCount);
    }

    // update page
    const newPage = Math.ceil(this.productData.count / this.pageSize) - 1;
    if (newPage !== currentPage) {
      this.pageIndex$.next(newPage);
      this.getProductList(newPage, undefined, undefined, callback);
    } else {
      this.emitProductChanges();
      callback();
    }
  }

  /**
   * Processes after delete
   * @param currentPage - The current page
   * @param sort - Sort object
   */
  private processDelete(currentPage: number, sort?: Sort) {
    const lastPage = Math.max(Math.ceil((this.productData.count - 1) / this.pageSize) - 1, 0);
    if (lastPage < currentPage) {
      // if we need to change the page
      this.pageIndex$.next(lastPage);
      this.getProductList(lastPage);
    } else {
      // just refresh the list because the items need to be re-sorted
      if (!sort) {
        this.getProductList(currentPage);
      } else {
        this.getProductList(currentPage, +SortColumn[sort.active], +SortDirection[sort.direction]);
      }
    }
  }

  /**
   * Checks if is not lead and get the payment summary
   * @param boolean isLead
   */
  checkIfNotLeadAndUpdateSummary() {
    this.paymentDetailsService.calculatePaymentSummary(this.quoteId);
  }

  printQuote(quoteSummary, displayRevision?) {
    const printWindow = this.exportUtilsService.openPrintFile();
    this.apiService.postBlob(this.urls.printQuoteUrl, this.mapPayload(quoteSummary, displayRevision))
      .subscribe((data: BlobPart) => this.exportUtilsService.printFile(data, printWindow));
  }

  exportQuote(quoteSummary, displayRevision?, isOrder?) {
    let fileName = isOrder ? `Order ${quoteSummary.quoteNumber}.xlsx` : `Quote ${quoteSummary.quoteNumber}.xlsx`;
    fileName = quoteSummary.quoteNumber ? fileName : `Quote ${quoteSummary.quoteRevisionNumber}.xlsx`;
    if (!this.exportInProgress) {
      this.exportInProgress = true;
      this.exportLoading$.next(true);
      this.apiService.postBlob(this.urls.exportQuoteUrl, this.mapPayload(quoteSummary, displayRevision))
        .subscribe((data: ArrayBuffer) => {
          this.exportUtilsService.downloadFile(data, fileName);
          this.exportInProgress = false;
          this.exportLoading$.next(false);
        }, () => {
          this.exportInProgress = false;
          this.exportLoading$.next(false);
        });
    }
  }

  emailQuote(quoteRevisionId, emailAddresses: string[], displayRevision?) {
    const payload = {
      quoteRevisionId,
      displayRevision,
      offset: (new Date()).getTimezoneOffset(),
      emailAddresses
    };
    return this.apiService.post(this.urls.emailQuoteUrl, payload);
  }

  private mapPayload(summary, displayRevision) {
    return {
      quoteRevisionId: displayRevision ? summary.quoteRevisionId : summary.id,
      offset: (new Date()).getTimezoneOffset(),
      displayRevision: Boolean(displayRevision)
    };
  }

  /**
   * Removes all products from Consigned Warehouse in a quote
   * @param quoteId - the quote ID
   * @param as shouldProcessDelete
   * @param as callback
   */
  deleteConsignedProductsFromQuote(quoteId: string, shouldProcessDelete?, callback?: (...args: any[]) => any) {
    const reqObj = {
      id: quoteId
    };

    this.apiService.post(this.urls.deleteConsignedProductsFromQuote, reqObj)
      .subscribe(() => {
        if (shouldProcessDelete) {
          this.processDelete(0);
          this.paymentDetailsService.calculatePaymentSummary(quoteId);
        }
        if (callback) {
          callback();
        }
      });
  }

  checkAtpBackorderNotification(productId: string, customerId: string) {
    return this.apiService.post(this.urls.atpNotification, {
      customerId,
      productId
    });
  }

  importQuotesOrders(quotesData) {
    return this.apiService.post(this.urls.importQuotesOrOrders, quotesData);
  }

  private adjustErrorCount(newErrorState, oldErrorState) {
    if (oldErrorState !== newErrorState) {
      if (oldErrorState) {
        // item had error, now it doesn't
        this.errorCount = this.errorCount - 1;
      } else {
        // item didn't have any error, now it has
        this.errorCount = this.errorCount + 1;
      }
      this.errorCount$.next(this.errorCount);
    }
  }

  getQuoteIdForNewQuotes() {
    return this.quoteId;
  }

  sendNetPriceChanges(quoteNumber, oldValue, newValue, productNumber) {
    const payload = {
      quoteNumber,
      previousUnitNetPrice: oldValue,
      newUnitNetPrice: newValue,
      productNumber
    };
    return this.apiService.post(this.urls.productNetPriceChanges, payload);

  }

  /**
   * Retrieve a list of products for the Grid Search
   * @param searchTerm search string
   * @param countryCodeId id of the customer country
   * @param page page number to retrieve
   */
  getProducts(searchTerm: string, countryCodeId: number, page = 0): Observable<{ count: number, items: GridSearchItem[] }> {
    const payload = {
      itemName: searchTerm,
      countryId: countryCodeId,
      page
    };

    return this.apiService.post(this.urls.getProductsUrl, payload);
  }

  /**
   * Triggers a recalculation of the ATP for the product list
   */
  refreshAtp() {
    return this.apiService.post(this.urls.refreshAtpUrl, { quoteId: this.quoteId });
  }

  /**
   * Refreshes the ATP for a single product
   * @param productId id (guid) of the product
   */
  refreshProductAtp(productId: string, callback?: () => void) {
    const existingProduct = this.productData.products.find(savedProduct => savedProduct.id === productId);
    if (existingProduct) {
      existingProduct.refreshingAtp = true;
      this.productList$.next(this.productData.products);
      this.apiService
        .post(this.urls.productShipDate, { id: productId, quoteId: this.quoteId, quantity: existingProduct.quantity })
        .subscribe((details: { id: string, estimatedShipDate: string }) => {
          existingProduct.estimatedShipDate = details.estimatedShipDate;
          existingProduct.refreshingAtp = false;
          this.productList$.next(this.productData.products);

          if (callback) {
            callback();
          }
        });
    }
  }

}
